import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getExams } from '../services/examService';
import { 
  ChartBarIcon, 
  PlusIcon, 
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  TrophyIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

const AllExams = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [exams, setExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter & Sort States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, TYT, AYT
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, net-desc, net-asc, score-desc, score-asc
  const [dateRange, setDateRange] = useState('all'); // all, last7, last30, last90
  
  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [exams, searchTerm, filterType, sortBy, dateRange]);

  const fetchExams = async () => {
    try {
      const response = await getExams();
      setExams(response.exams || []);
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let result = [...exams];

    // Search Filter
    if (searchTerm) {
      result = result.filter(exam => 
        exam.examName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.examType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        new Date(exam.examDate).toLocaleDateString('tr-TR').includes(searchTerm)
      );
    }

    // Type Filter
    if (filterType !== 'all') {
      result = result.filter(exam => exam.examType === filterType);
    }

    // Date Range Filter
    if (dateRange !== 'all') {
      const now = new Date();
      const daysAgo = {
        'last7': 7,
        'last30': 30,
        'last90': 90
      }[dateRange];
      
      const cutoffDate = new Date(now.setDate(now.getDate() - daysAgo));
      result = result.filter(exam => new Date(exam.examDate) >= cutoffDate);
    }

    // Sorting
    result.sort((a, b) => {
      switch(sortBy) {
        case 'date-desc':
          return new Date(b.examDate) - new Date(a.examDate);
        case 'date-asc':
          return new Date(a.examDate) - new Date(b.examDate);
        case 'net-desc':
          return b.totalNet - a.totalNet;
        case 'net-asc':
          return a.totalNet - b.totalNet;
        case 'score-desc':
          return (b.estimatedScore || 0) - (a.estimatedScore || 0);
        case 'score-asc':
          return (a.estimatedScore || 0) - (b.estimatedScore || 0);
        default:
          return 0;
      }
    });

    setFilteredExams(result);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const calculateStats = () => {
    if (filteredExams.length === 0) return null;
    
    const totalNet = filteredExams.reduce((sum, exam) => sum + exam.totalNet, 0);
    const avgNet = totalNet / filteredExams.length;
    
    const totalScore = filteredExams.reduce((sum, exam) => sum + (exam.estimatedScore || 0), 0);
    const avgScore = totalScore / filteredExams.length;
    
    const bestExam = filteredExams.reduce((best, exam) => 
      exam.totalNet > best.totalNet ? exam : best
    , filteredExams[0]);
    
    return {
      avgNet: avgNet.toFixed(2),
      avgScore: avgScore.toFixed(2),
      bestExam
    };
  };

  const stats = calculateStats();

  const getNetTrend = (exam, index) => {
    if (index === filteredExams.length - 1) return null;
    const prevExam = filteredExams[index + 1];
    const diff = exam.totalNet - prevExam.totalNet;
    
    if (diff > 0) return { type: 'up', value: diff.toFixed(2) };
    if (diff < 0) return { type: 'down', value: Math.abs(diff).toFixed(2) };
    return { type: 'same', value: 0 };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                title="Dashboard'a Dön"
              >
                <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
              </button>
              <Logo showText={true} className="h-10 w-10" textClassName="text-2xl font-bold text-gray-900" />
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-2xl font-bold text-gray-900">Tüm Denemeler</h1>
                <p className="text-sm text-gray-500">{filteredExams.length} deneme listeleniyor</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/add-exam"
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition shadow-sm"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Yeni Ekle
              </Link>
              <span className="text-sm text-gray-600">
                <span className="font-semibold">{user?.name}</span>
              </span>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-600 hover:text-gray-900 font-medium"
              >
                Çıkış
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-primary-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Toplam Deneme</p>
                  <p className="text-3xl font-bold text-gray-900">{filteredExams.length}</p>
                </div>
                <ChartBarIcon className="h-10 w-10 text-primary-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-success-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Ortalama Net</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.avgNet}</p>
                </div>
                <TrophyIcon className="h-10 w-10 text-success-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-warning-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Ortalama Puan</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.avgScore}</p>
                </div>
                <CalendarIcon className="h-10 w-10 text-warning-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-error-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">En İyi Net</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.bestExam.totalNet.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(stats.bestExam.examDate).toLocaleDateString('tr-TR')}
                  </p>
                </div>
                <TrophyIcon className="h-10 w-10 text-error-500" />
              </div>
            </div>
          </div>
        )}

        {/* Filters & Search */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MagnifyingGlassIcon className="h-4 w-4 inline mr-1" />
                Ara
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Deneme adı, tür, tarih..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FunnelIcon className="h-4 w-4 inline mr-1" />
                Deneme Türü
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">Tümü</option>
                <option value="TYT">TYT</option>
                <option value="AYT">AYT</option>
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Tarih Aralığı
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">Tüm Zamanlar</option>
                <option value="last7">Son 7 Gün</option>
                <option value="last30">Son 30 Gün</option>
                <option value="last90">Son 90 Gün</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <ChartBarIcon className="h-4 w-4 inline mr-1" />
                Sıralama
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="date-desc">Tarih (Yeni → Eski)</option>
                <option value="date-asc">Tarih (Eski → Yeni)</option>
                <option value="net-desc">Net (Yüksek → Düşük)</option>
                <option value="net-asc">Net (Düşük → Yüksek)</option>
                <option value="score-desc">Puan (Yüksek → Düşük)</option>
                <option value="score-asc">Puan (Düşük → Yüksek)</option>
              </select>
            </div>
          </div>

          {/* Active Filters Info */}
          {(searchTerm || filterType !== 'all' || dateRange !== 'all') && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-600">Aktif Filtreler:</span>
                  {searchTerm && (
                    <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-xs font-medium">
                      Arama: "{searchTerm}"
                    </span>
                  )}
                  {filterType !== 'all' && (
                    <span className="px-3 py-1 bg-success-100 text-success-800 rounded-full text-xs font-medium">
                      Tür: {filterType}
                    </span>
                  )}
                  {dateRange !== 'all' && (
                    <span className="px-3 py-1 bg-warning-100 text-warning-800 rounded-full text-xs font-medium">
                      Tarih: {
                        dateRange === 'last7' ? 'Son 7 Gün' :
                        dateRange === 'last30' ? 'Son 30 Gün' :
                        'Son 90 Gün'
                      }
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterType('all');
                    setDateRange('all');
                  }}
                  className="text-sm text-error-600 hover:text-error-700 font-medium"
                >
                  Filtreleri Temizle
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Exams List */}
        {filteredExams.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <ChartBarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">
              {exams.length === 0 ? 'Henüz deneme eklemediniz' : 'Filtrelerinize uygun deneme bulunamadı'}
            </p>
            <p className="text-gray-400 text-sm mb-6">
              {exams.length === 0 
                ? 'İlk denemenizi ekleyerek takibe başlayın' 
                : 'Farklı filtreler deneyebilir veya filtreleri temizleyebilirsiniz'
              }
            </p>
            {exams.length === 0 && (
              <Link
                to="/add-exam"
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition duration-150 font-semibold"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                İlk Denemeyi Ekle
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      #
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Tarih
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Deneme Adı
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Tür
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Toplam Net
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Trend
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Ders Netleri
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Puan
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredExams.map((exam, index) => {
                    const trend = getNetTrend(exam, index);
                    return (
                      <tr 
                        key={exam.id} 
                        onClick={() => navigate(`/exam/${exam.id}`)}
                        className="hover:bg-gray-50 cursor-pointer transition"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {filteredExams.length - index}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {new Date(exam.examDate).toLocaleDateString('tr-TR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {exam.examName || `Deneme ${exam.id}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            exam.examType === 'TYT' 
                              ? 'bg-primary-100 text-primary-800' 
                              : 'bg-success-100 text-success-800'
                          }`}>
                            {exam.examType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          {exam.totalNet.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {trend && trend.type !== 'same' && (
                            <div className={`flex items-center ${
                              trend.type === 'up' ? 'text-success-600' : 'text-error-600'
                            }`}>
                              {trend.type === 'up' ? (
                                <ArrowUpIcon className="h-4 w-4 mr-1" />
                              ) : (
                                <ArrowDownIcon className="h-4 w-4 mr-1" />
                              )}
                              <span className="font-semibold">{trend.value}</span>
                            </div>
                          )}
                          {trend && trend.type === 'same' && (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex gap-3">
                            {exam.nets?.turkish !== undefined && (
                              <span className="text-xs">
                                <span className="text-gray-500">TR:</span>
                                <span className="font-semibold ml-1">{exam.nets.turkish.toFixed(1)}</span>
                              </span>
                            )}
                            {exam.nets?.math !== undefined && (
                              <span className="text-xs">
                                <span className="text-gray-500">MT:</span>
                                <span className="font-semibold ml-1">{exam.nets.math.toFixed(1)}</span>
                              </span>
                            )}
                            {exam.nets?.science !== undefined && (
                              <span className="text-xs">
                                <span className="text-gray-500">FN:</span>
                                <span className="font-semibold ml-1">{exam.nets.science.toFixed(1)}</span>
                              </span>
                            )}
                            {exam.nets?.social !== undefined && (
                              <span className="text-xs">
                                <span className="text-gray-500">SS:</span>
                                <span className="font-semibold ml-1">{exam.nets.social.toFixed(1)}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {exam.estimatedScore ? exam.estimatedScore.toFixed(2) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer Info */}
        {filteredExams.length > 0 && (
          <div className="mt-6 text-center text-sm text-gray-500">
            Toplam {filteredExams.length} deneme görüntüleniyor
            {exams.length !== filteredExams.length && (
              <span> (Tüm denemeler: {exams.length})</span>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AllExams;
